import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { photoSrc } from "@/components/RoomPhotosUploader";
import { MapPin, Building2, Clock, Users2, CheckCircle2 } from "lucide-react";

const CITIES = { paris: "Paris", lyon: "Lyon", bordeaux: "Bordeaux" };

export default function CityLanding() {
  const { city } = useParams();
  const name = CITIES[city?.toLowerCase()] || (city ? city[0].toUpperCase() + city.slice(1) : "");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/city/${name}`).then((r) => setData(r.data));
    document.title = `Salles de sport à louer à ${name} — créneaux à l'heure pour coachs | TymCoach`;
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", `Louez une salle de sport ou un studio à ${name} à l'heure, sans engagement. Coachs : trouvez votre espace. Salles : rentabilisez vos créneaux vides. Offre de lancement ${name}.`);
    return () => { document.title = "TymCoach"; };
  }, [name]);

  return (
    <div>
      <Navbar />
      <section className="bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-5 gap-10 items-end">
          <div className="lg:col-span-3">
            <div className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-semibold flex items-center gap-2"><MapPin className="w-4 h-4" /> {name}</div>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05]" data-testid="city-title">Salles de sport à louer à {name}, à l'heure.</h1>
            <p className="mt-6 text-base md:text-lg text-slate-300 max-w-xl">Coachs : réservez un studio ou une salle à {name} sans loyer mensuel. Salles : transformez vos heures creuses en revenus, vous validez chaque demande.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link data-testid="city-cta-coach" to={`/carte?city=${name}`} className="sc-btn sc-btn-emerald">Voir les créneaux à {name}</Link>
              <Link data-testid="city-cta-manager" to="/inscription?role=room_manager" className="sc-btn bg-white text-slate-900 hover:bg-slate-100">Je publie ma salle</Link>
            </div>
          </div>
          <div className="lg:col-span-2 grid grid-cols-3 gap-3" data-testid="city-stats">
            {[[Building2, data?.rooms.length ?? "…", "salles"], [Clock, data?.free_slots ?? "…", "créneaux libres"], [Users2, data?.coaches ?? "…", "coachs"]].map(([I, v, l]) => (
              <div key={l} className="rounded-2xl bg-white/5 border border-white/10 p-4"><I className="w-4 h-4 text-emerald-400" /><div className="mt-2 text-3xl font-extrabold">{v}</div><div className="text-xs text-slate-400">{l}</div></div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="rounded-3xl border border-emerald-200 bg-emerald-50/60 p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-6 justify-between" data-testid="city-offer">
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-emerald-700">Offre de lancement {name}</div>
            <h2 className="mt-1 text-base md:text-lg font-bold text-slate-900">0 % de frais de service pendant 3 mois pour les salles qui publient leurs créneaux</h2>
            <ul className="mt-3 text-sm text-slate-700 space-y-1">
              {["Vous fixez votre tarif horaire et validez chaque coach", "Paiement sécurisé, reversé à 100 %", "Créneaux récurrents publiés en un clic"].map((t) => <li key={t} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> {t}</li>)}
            </ul>
          </div>
          <Link to="/inscription?role=room_manager" className="sc-btn sc-btn-emerald shrink-0">Publier ma salle à {name}</Link>
        </div>

        <h2 className="mt-16 text-2xl font-bold text-slate-900">Salles disponibles à {name}</h2>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="city-rooms">
          {data && data.rooms.length === 0 && <div className="text-slate-500 text-sm">Soyez la première salle référencée à {name} — <Link className="underline" to="/inscription?role=room_manager">publiez vos créneaux</Link>.</div>}
          {data?.rooms.map((r) => (
            <Link key={r.id} to={`/salles/${r.id}`} data-testid={`city-room-${r.id}`} className="sc-card p-0 overflow-hidden hover:-translate-y-0.5 transition-transform">
              <div className="h-40 bg-emerald-500">{r.photo_url && <img src={photoSrc(r.photo_url)} alt={r.name} className="w-full h-full object-cover" />}</div>
              <div className="p-4"><div className="font-bold text-slate-900">{r.name}</div><div className="text-xs text-slate-500 mt-1">{r.neighborhood || r.city} · {r.price_per_hour}€/h</div></div>
            </Link>
          ))}
        </div>

        <div className="mt-16 flex flex-wrap gap-2 text-sm text-slate-600">
          Autres villes : {Object.entries(CITIES).filter(([k]) => k !== city?.toLowerCase()).map(([k, v]) => <Link key={k} to={`/salles-de-sport/${k}`} className="underline hover:text-slate-900">{v}</Link>)}
        </div>
      </section>
      <Footer />
    </div>
  );
}
